// Period normalisation + arithmetic. Used both client-side (input
// validation, display) and mirrored in netlify/functions/_lib/period.ts
// for backend lookups. Keep them in sync.
//
// Canonical formats:
//   '2026-Q1' / '2026-Q2' / '2026-Q3' / '2026-Q4'  — calendar quarters
//   '2026-FY'                                       — annual filings (10-K)
//
// We deliberately use *calendar* periods, not fiscal periods, so cohort
// comparisons are apples-to-apples across companies with different fiscal
// year-ends (Apple's Q2 FY26 ends in March 2026 = 2026-Q1 calendar).

export type PeriodKind = 'quarter' | 'annual';

export interface ParsedPeriod {
  year: number;
  kind: PeriodKind;
  /** 1–4 for quarterly, undefined for annual. */
  quarter?: number;
  canonical: string;
}

const PERIOD_RE = /^(\d{4})-(Q[1-4]|FY)$/;

export function parsePeriod(input: string | undefined | null): ParsedPeriod | null {
  if (!input) return null;
  const trimmed = input.trim().toUpperCase();
  const m = PERIOD_RE.exec(trimmed);
  if (!m) return null;
  const year = parseInt(m[1]!, 10);
  const tail = m[2]!;
  if (tail === 'FY') {
    return { year, kind: 'annual', canonical: `${year}-FY` };
  }
  const q = parseInt(tail.slice(1), 10);
  return { year, kind: 'quarter', quarter: q, canonical: `${year}-Q${q}` };
}

/** Best-effort normalisation of free-form input ("Q2 26", "Q2 2026", "2026q2",
 *  "FY26", etc.) to canonical form. Returns the original string if we can't
 *  recognise it — caller decides whether to accept invalid input. */
export function normalizePeriod(input: string): string {
  const s = input.trim().toUpperCase().replace(/\s+/g, '');
  // Already canonical?
  if (PERIOD_RE.test(s)) return s;
  // Patterns: Q2-2026, Q22026, 2026Q2, 26Q2, Q226
  let m = /^(\d{4})Q([1-4])$/.exec(s);
  if (m) return `${m[1]}-Q${m[2]}`;
  m = /^Q([1-4])-?(\d{4})$/.exec(s);
  if (m) return `${m[2]}-Q${m[1]}`;
  m = /^Q([1-4])-?(\d{2})$/.exec(s);
  if (m) return `20${m[2]}-Q${m[1]}`;
  m = /^FY-?(\d{4})$/.exec(s);
  if (m) return `${m[1]}-FY`;
  m = /^FY-?(\d{2})$/.exec(s);
  if (m) return `20${m[1]}-FY`;
  m = /^(\d{4})$/.exec(s);
  if (m) return `${m[1]}-FY`;
  return input.trim();
}

/** Returns the canonical period one step before the given period.
 *  Q2 → Q1, Q1 → previous-year Q4, FY → previous-year FY. */
export function priorPeriod(canonical: string): string | null {
  const p = parsePeriod(canonical);
  if (!p) return null;
  if (p.kind === 'annual') return `${p.year - 1}-FY`;
  if (p.quarter === 1) return `${p.year - 1}-Q4`;
  return `${p.year}-Q${(p.quarter ?? 1) - 1}`;
}

export function formatPeriod(canonical: string | undefined | null): string {
  if (!canonical) return '';
  const p = parsePeriod(canonical);
  if (!p) return canonical;
  if (p.kind === 'annual') return `FY${String(p.year).slice(-2)}`;
  return `Q${p.quarter} ${p.year}`;
}
